import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, Users } from 'lucide-react';
import GlowBackground from '../components/GlowBackground';
import Logo from '../components/Logo';
import BurgerMenu from '../components/BurgerMenu';
import RatingPopup from '../components/RatingPopup';
import { useAuth } from '../context/AuthContext';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WavelengthSession = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [session, setSession] = useState(null);
  const [thought, setThought] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId] = useState(() => `user-${Math.random().toString(36).substr(2, 9)}`);
  const [myVote, setMyVote] = useState(null);
  const [partnerVote, setPartnerVote] = useState(null);
  const [participantCount, setParticipantCount] = useState(1);
  const [copied, setCopied] = useState(false);
  const [showMergeAnimation, setShowMergeAnimation] = useState(false);
  const [glowMerged, setGlowMerged] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [thoughtFading, setThoughtFading] = useState(false);
  const [showWavelengthRating, setShowWavelengthRating] = useState(false);
  const [pendingNextThought, setPendingNextThought] = useState(null);
  const [ratedThoughtId, setRatedThoughtId] = useState(null);
  const [showWlHint, setShowWlHint] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const partnerGraceRef = useRef(null);
  const hasPartnerJoined = useRef(false);
  const myVoteRef = useRef(null);
  const partnerVoteRef = useRef(null);
  const thoughtRef = useRef(null);

  // Keep refs in sync with state for WebSocket callback access
  useEffect(() => { myVoteRef.current = myVote; }, [myVote]);
  useEffect(() => { partnerVoteRef.current = partnerVote; }, [partnerVote]);
  useEffect(() => { thoughtRef.current = thought; }, [thought]);

  // Record wavelength points
  const recordWavelengthPoints = useCallback((pointType) => {
    if (!isAuthenticated || !thought) return;
    axios.post(`${API}/wavelength/points?point_type=${pointType}&thought_id=${thought.id}&session_slug=${slug}`)
      .catch(() => {});
  }, [isAuthenticated, thought, slug]);

  // Show wavelength hint once both participants are present
  useEffect(() => {
    if (participantCount >= 2) {
      try {
        const dismissed = localStorage.getItem('rc_wl_hint_dismissed');
        if (!dismissed) setShowWlHint(true);
      } catch {}
    }
  }, [participantCount]);

  const dismissWlHint = useCallback(() => {
    if (!showWlHint) return;
    try { localStorage.setItem('rc_wl_hint_dismissed', '1'); } catch {}
    setShowWlHint(false);
  }, [showWlHint]);

  // Generate share URL
  const shareUrl = `${window.location.origin}/wavelength/${slug}`;

  // Copy link handler with fallback
  const copyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      toast.success('Link copied!', { description: 'Share it with your partner.' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Could not copy link');
    }
  };

  // Fetch session data
  const fetchSession = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/wavelength/${slug}/join`);
      setSession(response.data);
      setThought(response.data.current_thought);
      setIsLoading(false);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Session not found');
        navigate('/');
      } else {
        console.error('Error fetching session:', error);
        toast.error('Could not load session');
      }
      setIsLoading(false);
    }
  }, [slug, navigate]);

  // WebSocket connection with improved error handling
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    // Close any existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Construct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = new URL(BACKEND_URL).host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/${slug}/${userId}`;
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 10000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'pong') {
            // Keep-alive response, ignore
            return;
          }
          
          if (data.type === 'participant_update') {
            if (data.action === 'joined' && data.user_id !== userId) {
              // Clear any pending grace timeout
              if (partnerGraceRef.current) {
                clearTimeout(partnerGraceRef.current);
                partnerGraceRef.current = null;
              }
              hasPartnerJoined.current = true;
              setParticipantCount(data.count);
              toast.success('Partner joined!', { description: 'You\'re now on the same wavelength.' });
            }
            if (data.action === 'left' && data.user_id !== userId) {
              // Grace period: wait before showing "waiting" to handle brief reconnections
              if (partnerGraceRef.current) clearTimeout(partnerGraceRef.current);
              partnerGraceRef.current = setTimeout(() => {
                setParticipantCount(data.count);
                setPartnerVote(null);
                setShowMergeAnimation(false);
                setGlowMerged(false);
                toast.info('Partner left the session');
                partnerGraceRef.current = null;
              }, 5000);
            }
            if (data.action === 'joined' && data.user_id === userId) {
              setParticipantCount(data.count);
              if (data.count >= 2) hasPartnerJoined.current = true;
            }
          }
          
          if (data.type === 'vote_update') {
            if (data.user_id !== userId) {
              setPartnerVote(data.vote);
            }
            
            // Check for both voted scenarios
            if (data.both_voted) {
              // Record wavelength points if authenticated
              const myV = data.user_id === userId ? data.vote : myVoteRef.current;
              const partnerV = data.user_id !== userId ? data.vote : partnerVoteRef.current;
              
              if (data.both_yes) {
                // Both said yes - show action buttons + glow merge
                setShowMergeAnimation(true);
                setGlowMerged(true);
                toast.success('You\'re both ready!', { description: 'Talk about this thought.' });
                recordWavelengthPoints('sync');
                
                // Glow merge disappears after 2 seconds (buttons stay)
                setTimeout(() => {
                  setGlowMerged(false);
                }, 2000);
              } else {
                // Check if I voted yes but partner said no = courage
                if (myV === true && partnerV === false) {
                  recordWavelengthPoints('courage');
                } else if (myV === false && partnerV === false) {
                  recordWavelengthPoints('presence');
                }
                // Reset votes, let thought_update handle the transition
                setMyVote(null);
                setPartnerVote(null);
              }
            }
          }
          
          if (data.type === 'thought_update') {
            setThoughtFading(true);
            setTimeout(() => {
              setThought(data.thought);
              setMyVote(null);
              setPartnerVote(null);
              setShowMergeAnimation(false);
              setGlowMerged(false);
              setThoughtFading(false);
            }, 400);
          }
          
          if (data.type === 'talked_complete') {
            setRatedThoughtId(thoughtRef.current?.id || null);
            setPendingNextThought(data.next_thought);
            setShowWavelengthRating(true);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        // Attempt to reconnect after delay (only if not intentionally closed)
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connectWebSocket();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
    }
  }, [slug, userId]);

  // Initialize
  useEffect(() => {
    fetchSession();
    
    // Small delay before connecting WebSocket to ensure session exists
    const wsTimeout = setTimeout(() => {
      connectWebSocket();
    }, 500);

    return () => {
      clearTimeout(wsTimeout);
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (partnerGraceRef.current) {
        clearTimeout(partnerGraceRef.current);
      }
    };
  }, [fetchSession, connectWebSocket]);

  // Rating handlers for Wavelength
  const handleWavelengthRatingSubmit = (score) => {
    if (ratedThoughtId) {
      axios.post(`${API}/thoughts/rate`, { thought_id: ratedThoughtId, score }).catch(() => {});
    }
  };

  const handleWavelengthRatingClose = () => {
    setShowWavelengthRating(false);
    setShowMergeAnimation(false);
    setGlowMerged(false);
    if (pendingNextThought) {
      setThoughtFading(true);
      setTimeout(() => {
        setThought(pendingNextThought);
        setMyVote(null);
        setPartnerVote(null);
        setThoughtFading(false);
        setPendingNextThought(null);
        toast.success('Great conversation!', { description: 'Here\'s another thought.' });
      }, 400);
    }
  };

  // Vote handler
  const handleVote = (vote) => {
    if (myVote !== null) return; // Already voted
    dismissWlHint();
    setMyVote(vote);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'vote',
        vote
      }));
    } else {
      // Fallback to REST API
      axios.post(`${API}/wavelength/${slug}/vote`, {
        user_id: userId,
        vote
      }).catch(err => console.error('Vote failed:', err));
    }
  };

  // "We talked about this" handler
  const handleTalked = () => {
    dismissWlHint();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'talked'
      }));
    }
  };

  // Skip to next question
  const handleNext = () => {
    dismissWlHint();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'next_thought'
      }));
    }
  };

  // Determine glow states
  const leftActive = myVote === true;
  const rightActive = partnerVote === true;
  const merged = glowMerged && myVote === true && partnerVote === true;

  // Get level badge
  const getLevelBadge = (level) => {
    const badges = {
      spark: { class: 'badge-spark', label: 'Spark' },
      depth: { class: 'badge-depth', label: 'Depth' },
      breakthrough: { class: 'badge-breakthrough', label: 'Breakthrough' }
    };
    return badges[level] || badges.spark;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <GlowBackground />
        <div className="content-layer w-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto mb-4" />
            <p className="text-charcoal/60">Loading session...</p>
          </div>
        </div>
      </div>
    );
  }

  const badge = thought ? getLevelBadge(thought.level) : getLevelBadge('spark');

  return (
    <div 
      className="min-h-screen bg-cream relative overflow-hidden"
      data-testid="wavelength-session-page"
    >
      {/* Split glow background — cursor-following when alone, split when paired */}
      <GlowBackground 
        splitMode={participantCount >= 2}
        leftActive={leftActive}
        rightActive={rightActive}
        merged={merged}
      />
      <div className="content-layer">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 p-6 md:p-8 flex items-center justify-between z-10">
        <Logo size="default" onClick={() => navigate('/')} />
        
        <div className="flex items-center gap-4">
          {/* Participant count */}
          <div 
            className="flex items-center gap-2 text-charcoal/60 text-sm"
            data-testid="participant-count"
          >
            <Users size={16} />
            <span>{participantCount}/2</span>
            {isConnected && (
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            )}
          </div>
          
          {/* Copy link button */}
          <button
            onClick={copyLink}
            className="btn-ghost flex items-center gap-2 text-sm"
            data-testid="copy-link-button"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>

          {/* Burger menu */}
          <BurgerMenu />
        </div>
      </header>
      
      {/* Main content */}
      <main className="min-h-screen flex flex-col items-center justify-center pt-24 pb-48 px-6">
        {/* Session slug — only while waiting */}
        <AnimatePresence>
          {participantCount < 2 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8, transition: { duration: 0.5 } }}
              className="mb-8"
            >
              <span
                className="text-sm tracking-[0.2em] uppercase text-orange-primary/70 font-medium"
                data-testid="session-slug"
              >
                {slug}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wavelength hint — shown once both are present, dismissed on first action */}
        <AnimatePresence>
          {participantCount >= 2 && showWlHint && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
              className="mb-6 text-center max-w-xl px-4"
              data-testid="wl-onboarding-hint"
            >
              <span
                style={{
                  fontFamily: "'Caveat', cursive",
                  fontWeight: 600,
                  fontSize: 'clamp(1.1rem, 2.8vw, 1.35rem)',
                  color: 'rgba(44, 36, 23, 0.38)',
                  letterSpacing: '0.01em',
                  lineHeight: 1.4,
                  display: 'inline-block',
                  transform: 'rotate(-0.3deg)',
                }}
              >
              You're both seeing this. Decide if this is the question you want to answer.
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Thought display with fade animation */}
        <AnimatePresence mode="wait">
          {thought && (
            <motion.div
              key={thought.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: thoughtFading ? 0 : 1, 
                y: thoughtFading ? -20 : 0,
                scale: thoughtFading ? 0.95 : 1
              }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-center max-w-4xl mb-12"
            >
              <h1 
                className="thought-text text-charcoal text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight mb-8"
                data-testid="wavelength-thought-content"
              >
                {thought.content}
              </h1>
              
              <span 
                className={badge.class}
                data-testid="wavelength-thought-badge"
              >
                {badge.label}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Waiting message — breathing pulse with orange glow */}
        {participantCount < 2 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-8"
          >
            <motion.p
              animate={{
                opacity: [0.45, 0.8, 0.45],
                textShadow: [
                  '0 0 0px rgba(255, 120, 10, 0)',
                  '0 0 16px rgba(255, 120, 10, 0.28)',
                  '0 0 0px rgba(255, 120, 10, 0)',
                ],
              }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
              className="text-charcoal/60 text-base mb-4"
              data-testid="waiting-message"
            >
              Waiting for your partner to join...
            </motion.p>
            <p className="text-charcoal/40 text-sm">
              Share the link above to invite someone
            </p>
          </motion.div>
        )}
      </main>
      
      {/* Voting / Action area */}
      <div className="fixed bottom-0 left-0 right-0 p-6 md:p-8" data-testid="wavelength-controls">
        <div className="max-w-4xl mx-auto">
          {/* Show voting buttons when both participants present and not yet decided */}
          {participantCount >= 2 && !showMergeAnimation && !showWavelengthRating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center"
            >
              {/* Dots + labels — always fixed here, never move */}
              <div className="flex items-center gap-10 mb-6">
                <div className="flex flex-col items-center gap-2">
                  <motion.div
                    animate={{ scale: myVote !== null ? 1.3 : 1, backgroundColor: myVote !== null ? '#ff780a' : 'rgba(44,36,23,0.3)' }}
                    className="w-3 h-3 rounded-full transition-[transform,background-color] duration-500"
                  />
                  <span className="text-xs text-charcoal/60">You</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <motion.div
                    animate={{ scale: partnerVote !== null ? 1.3 : 1, backgroundColor: partnerVote !== null ? '#ff780a' : 'rgba(44,36,23,0.3)' }}
                    className="w-3 h-3 rounded-full transition-[transform,background-color] duration-500"
                  />
                  <span className="text-xs text-charcoal/60">Partner</span>
                </div>
              </div>

              {/* Buttons area — or post-vote status */}
              {myVote === null ? (
                <div className="flex items-start gap-4">
                  {/* Left: annotation + Not this one */}
                  <div className="flex flex-col items-center">
                    <AnimatePresence>
                      {showWlHint && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: 0.5 }}
                          className="flex flex-col items-center mb-2"
                        >
                          <span style={{ fontFamily: "'Caveat', cursive", fontSize: '1.05rem', color: 'rgba(44,36,23,0.42)', whiteSpace: 'nowrap' }}>
                            Swap it out
                          </span>
                          <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                            <path d="M10 2 L10 12" stroke="rgba(44,36,23,0.3)" strokeWidth="1.5" strokeLinecap="round" />
                            <path d="M6 9 L10 14 L14 9" stroke="rgba(44,36,23,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          </svg>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleVote(false)}
                      className="btn-ghost px-8 py-4 text-base"
                      data-testid="vote-no-button"
                    >
                      Not this one
                    </motion.button>
                  </div>

                  {/* Right: annotation + I'm ready for this */}
                  <div className="flex flex-col items-center">
                    <AnimatePresence>
                      {showWlHint && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: 0.5 }}
                          className="flex flex-col items-center mb-2"
                        >
                          <span style={{ fontFamily: "'Caveat', cursive", fontSize: '1.05rem', color: 'rgba(44,36,23,0.42)', whiteSpace: 'nowrap' }}>
                            Say yes to this one
                          </span>
                          <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                            <path d="M10 2 L10 12" stroke="rgba(44,36,23,0.3)" strokeWidth="1.5" strokeLinecap="round" />
                            <path d="M6 9 L10 14 L14 9" stroke="rgba(44,36,23,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          </svg>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleVote(true)}
                      className="btn-primary px-8 py-4 text-base"
                      data-testid="vote-yes-button"
                    >
                      I'm ready for this
                    </motion.button>
                  </div>
                </div>
              ) : (
                <p className="text-charcoal/60 text-sm">
                  {myVote ? 'Waiting for your partner...' : 'Skipping...'}
                </p>
              )}
            </motion.div>
          )}
          
          {/* Show rating popup after "We talked about this" */}
          {showWavelengthRating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center pb-2"
            >
              <RatingPopup
                isOpen={showWavelengthRating}
                onSubmit={handleWavelengthRatingSubmit}
                onClose={handleWavelengthRatingClose}
              />
            </motion.div>
          )}

          {/* Show action buttons when both said yes */}
          {showMergeAnimation && !showWavelengthRating && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <p 
                className="text-charcoal/60 mb-6"
                style={{ fontFamily: "'Caveat', cursive", fontSize: '1.1rem' }}
              >
                You're both ready
              </p>
              
              {/* Next thought - matching homepage style */}
              <button
                onClick={handleNext}
                className="text-charcoal/50 hover:text-charcoal transition-colors duration-300 text-sm flex items-center gap-1.5 mb-5"
                data-testid="wavelength-skip-button"
              >
                Next thought
                <svg width="22" height="12" viewBox="0 0 22 12" fill="none" className="mt-px">
                  <path d="M2 6 C 6 6, 10 6, 15 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  <path d="M13 3 L 17 6 L 13 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </button>

              {/* We talked about this - matching homepage pill button */}
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(255, 120, 10, 0.25)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleTalked}
                className="px-8 py-4 rounded-full font-medium text-white transition-[box-shadow,transform,background-color,opacity] duration-300"
                style={{
                  background: '#ff780a',
                  boxShadow: '0 4px 20px rgba(255, 120, 10, 0.3)',
                }}
                data-testid="wavelength-talked-button"
              >
                We talked about this
              </motion.button>
            </motion.div>
          )}
          
          {/* Show waiting state when alone */}
          {participantCount < 2 && (
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/')}
                className="text-charcoal/60 hover:text-charcoal text-sm transition-colors"
              >
                ← Leave session
              </button>
            </div>
          )}
        </div>
      </div>
      
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#FFF8F0',
            border: '1px solid rgba(255, 120, 10, 0.2)',
            color: '#2C2417',
          },
        }}
      />
      </div>{/* end content-layer */}
    </div>
  );
};

export default WavelengthSession;
